import React, { useEffect, useState } from "react";
import './index.css';

import { useApolloClient } from "@apollo/client";

import { Toast } from "primereact/toast";

/** components */
import Map from "../../components/map";

/** contexts */
import { useSettings } from "../../context/settings";
import { useUser } from "../../context/users";

/** QUERIES MUTATIONS */
import FRANCHISES from "../../provider/Franchises/query";
import { REGIONS } from "../../provider/Regions/query";

/** utils */
import { getGraphsqErros } from "../../utilities/functions";
import { Dropdown } from "primereact/dropdown";
import { DRIVERS } from "../../provider/Drivers/query";

const Crud = () => {
  const settings = useSettings();
  const apollo = useApolloClient();

  const user = useUser();
  const franchise_id = user?.getUser()?.franchise?.id;
  const acting_region_id = user?.getUser()?.actingRegion?.id;

  const toast = React.useRef<any>(null);

  const [loading, setLoading] = useState<boolean>(true);

  const initialCords: iCordinates = {
    latitude: -8.75,
    longitude: -63.88,
  }

  const [localCordinates, setLocalCordinates] = useState<iCordinates>(initialCords);

  const [driversOn, setDriversOn] = useState<iCarMarkers[]>([]);
  
  const [franchises, setFranchises] = useState<iFranchise[]>();
  const [selectedFranchise, setSelectedFranchise] = useState<iFranchise>();
  const [loadingFranchises, setLoadingFranchises] = useState(true);
  
  const [regions, setRegions] = useState<iRegion[]>();
  const [selectedRegion, setSelectedRegion] = useState<iRegion>();
  const [loadingRegions, setLoadingRegions] = useState(true);

  const loadFranchises = () => {
    setLoadingFranchises(true)

    apollo
      .query({
        query: FRANCHISES,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.franchises;

        setFranchises(data);
        setLoadingFranchises(false);
      })
      .catch(error => {
        console.log('FRANCHISES error: ', JSON.stringify(error));
        setFranchises([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  };

  const loadRegions = () => {
    setLoadingRegions(true)

    const variables: any = {}

    if (franchise_id) variables.franchise_id = Number(franchise_id);

    if (selectedFranchise?.id) variables.franchise_id = Number(selectedFranchise.id);

    apollo
      .query({
        query: REGIONS,
        variables,
        fetchPolicy: "no-cache",
      })
      .then(response => {
        const data = response.data.regions;

        setRegions(data);
        setLoadingRegions(false);
      })
      .catch(error => {
        console.log('REGIONS error: ', JSON.stringify(error));
        setRegions([]);
        settings?.setErrorMessage(getGraphsqErros(error));
        settings?.setVisibleError(true);
      });
  }

  const loadDriversOn = async () => {
    const filter: any = {
      is_online: true,
      state: 'APPROVED',
    };

    if (franchise_id) filter.franchise_id = Number(franchise_id);
    if (selectedFranchise?.id) filter.franchise_id = Number(selectedFranchise.id);

    if (acting_region_id) filter.acting_region_id = Number(acting_region_id);
    if (selectedRegion?.id) filter.acting_region_id = Number(selectedRegion.id);

    setLoading(true);

    apollo
      .query({
        query: DRIVERS,

        variables: { filter },

        fetchPolicy: "no-cache",
      })
      .then((response: any) => {
        const drivers: iDriver[] = response.data.drivers;

        if (drivers.length === 0) setDriversOn([]);

        if (drivers[0]?.latitude && drivers[0]?.longitude) setLocalCordinates({
          latitude: Number(drivers[0].latitude),
          longitude: Number(drivers[0].longitude),
        });

        setDriversOn(drivers.map((driver) => {
          return {
            name: driver.name,
            phone: driver.phone,
            latitude: Number(driver.latitude),
            longitude: Number(driver.longitude),
            className: 'pi pi-car',
            color: driver.active_booking ? 'red' : 'blue'
          }
        }));

        setLoading(false);
      })
      .catch(e => {
        console.log('load DRIVERS error: ', JSON.stringify(e));
      });
  }

  useEffect(() => {
    if (!franchise_id) loadFranchises();
  }, []);

  useEffect(() => {
    if (!acting_region_id) loadRegions();
  }, [selectedFranchise?.id]);

  useEffect(() => {
    loadDriversOn();
  }, [selectedFranchise?.id, selectedRegion?.id])

  return (
    <div className="card">
      <Toast ref={toast} />

      <div
        className="flex flex-column md:flex-row flex-wrap "
        style={{flexDirection: 'row'}}
      >
        {!franchise_id ? (
          <div className="card">
            <h5>Franquia</h5>

            {loadingFranchises ? (
              <>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                <span> Carregando franquias... </span>
              </>
            ) : (
              <Dropdown
                value={selectedFranchise}
                onChange={e => setSelectedFranchise(e.value)}
                options={franchises}
                optionLabel="name"
                placeholder="Selecione uma Franquia"
              />
            )}
          </div>
        ) : null}

        {!acting_region_id ? (
          <div className="card">
            <h5>Região</h5>

            {loadingRegions ? (
              <>
                <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
                <span> Carregando regiões... </span>
              </>
            ) : (
              <Dropdown
                value={selectedRegion}
                onChange={e => setSelectedRegion(e.value)}
                options={regions}
                optionLabel="name"
                placeholder="Selecione uma Região"
              />
            )}
          </div>
        ) : null}
      </div>

      <div className="card">
        {loading ? (
          <>
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '1rem' }} />
            <span> Carregando... </span>
          </>
        ) : driversOn.length ? (
          <>
            <div style={{marginBottom: 10}}>
              <span>
                Motoristas online: {driversOn.length} Ocupados: {driversOn.reduce((accumulator, currentValue) => {
                  if (currentValue.color === 'red') return accumulator + 1;

                  return accumulator;
                }, 0)}
              </span>
            </div>
            <Map
              latitude={localCordinates.latitude}
              longitude={localCordinates.longitude}
              callBack={() => {}}
              hideMark
              markList={driversOn}
            />
          </>
        ) : (
          <span>
            Não há motoristas online.
          </span>
        )}
      </div>
    </div>
  );
};

export default Crud;

interface iCordinates {
  latitude: number,
  longitude: number,
}
